import dayjs from 'dayjs';

export const startOfMonth = (date) => dayjs(date).startOf('month').toDate();

export const nextMonth = (date) =>
  dayjs(date).add(1, 'month').startOf('month').toDate();

const earliest = (a, b) => (dayjs(a).isBefore(b) ? a : b);
const latest = (a, b) => (dayjs(a).isAfter(b) ? a : b);

// Whole-month selection for a month picked on the main calendar: day 1 to the
// last day, or to today when the month is the current one (a month never ends
// after `now`).
export const getMonthRange = (month, now = new Date()) => ({
  startDate: startOfMonth(month),
  endDate: earliest(dayjs(month).endOf('month').startOf('day').toDate(), now),
});

// Months the panels show after a selection that did not come from a day click.
// Left: month of the range start, capped at the current month. Right: the month
// after the left one, which may be a future month. A single panel (narrow
// screens) shows the month of the range start.
export const getFocusMonths = (
  start,
  end,
  now = new Date(),
  single = false,
) => {
  const currentMonth = startOfMonth(now);
  const left = earliest(startOfMonth(start), currentMonth);
  if (single) {
    return { left, right: currentMonth };
  }
  return { left, right: nextMonth(left) };
};

// True when the months satisfy left < right and left <= current month.
export const isValidPanelMonths = ({ left, right }, now = new Date()) =>
  dayjs(left).isBefore(right, 'month') && !dayjs(left).isAfter(now, 'month');

// The left panel reaches the current month at most. The right panel starts at
// the month after the left one and can be moved up to the end of the current
// year, but its days after today cannot be selected: `maxDate` bounds the
// navigation and the year list, `disabledDay` the days. With the left panel on
// December of the current year the only month left for the right one is next
// January, so the limit stretches to it.
export const getPanelBounds = ({ left }, now = new Date()) => {
  const minDate = nextMonth(left);
  return {
    left: { maxDate: now },
    right: {
      minDate,
      maxDate: latest(
        dayjs(now).endOf('year').toDate(),
        dayjs(minDate).endOf('month').toDate(),
      ),
      disabledDay: (day) => dayjs(day).isAfter(now, 'day'),
    },
  };
};

// True when the whole month of `year`/`month` lies outside [minDate, maxDate].
export const isMonthOutOfBounds = (year, month, minDate, maxDate) => {
  const first = new Date(year, month, 1);
  const last = dayjs(first).endOf('month').toDate();
  return Boolean((minDate && last < minDate) || (maxDate && first > maxDate));
};
